// Onglet « Cheminements » : passages physiques des câbles sur un fond de plan (PDF ou image du dossier).
// Calibration de l'échelle (proposée puis validée), tracé point par point, longueurs calculées par le serveur, quantités vers le devis.
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, FileImage, Maximize2, PenLine, Plus, Ruler, Trash2, Undo2, X, ZoomIn, ZoomOut } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { dateHeure } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, Dialogue, EtatErreur, EtatVide, LienBouton, Panneau, useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { VisionneusePlan, type Outil, type VisionneusePoignee } from './VisionneusePlan.tsx';
import { TableauCheminements } from './TableauCheminements.tsx';
import { RecapQuantites } from './RecapQuantites.tsx';
import { TYPES_CABLE_SUGGERES, lireNombre, type CheminementVue, type PieceDisponible, type PlanVue, type Point } from './modele.ts';
import { ChampsFrigorifiques, ChampsVerticale, DialogueParametres, RattachementEtage, liaisonVide, verticaleVide } from './ParametresFrigorifiques.tsx';
import type { Refrigeration, VerticalSpec } from '../../../../shared/frigorifique.ts';

const AIDE_FORMATS = 'Fonds de plan acceptés : PDF et images (PNG, JPEG, WebP) déposés dans les pièces du dossier. Les fichiers DWG, IFC et les fichiers de programmation ne sont pas lus : exportez d’abord le plan en PDF.';

export function OngletCheminements({ dossierId }: { dossierId: string }) {
  const { peut } = useSession();
  const ecriture = peut('conception.ecrire');
  const [params, setParams] = useSearchParams();
  const [nouveau, setNouveau] = useState(false);
  const q = useQuery({ queryKey: ['conception-plans', dossierId], queryFn: () => api.get<{ plans: PlanVue[]; piecesDisponibles: PieceDisponible[] }>(`/api/dossiers/${dossierId}/plans`) });

  if (q.isLoading) return <Chargement lignes={4} libelle="Chargement des plans…" />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const { plans, piecesDisponibles } = q.data;
  const planId = params.get('plan') && plans.some((p) => p.id === params.get('plan')) ? params.get('plan')! : plans[0]?.id;
  const plan = plans.find((p) => p.id === planId);
  const choisirPlan = (id: string) => { const n = new URLSearchParams(params); n.set('plan', id); setParams(n, { replace: true }); };

  return (
    <div className="pile-l">
      <div className="cch-barre">
        {plans.length > 0 && (
          <div className="cch-choix-plan">
            <ChampChoix libelle="Plan" value={planId} onChange={(e) => choisirPlan(e.target.value)}
              options={plans.map((p) => ({ valeur: p.id, libelle: `${p.nom}${p.piece ? ` — ${p.piece.titre}, p. ${p.page}` : ''}` }))} />
          </div>
        )}
        {ecriture && piecesDisponibles.length > 0 && <Bouton icone={<Plus aria-hidden />} variante={plans.length ? 'secondaire' : 'principal'} onClick={() => setNouveau(true)}>Nouveau plan</Bouton>}
        <Bouton onClick={() => document.getElementById('charge-frigo')?.scrollIntoView({ block: 'start' })}>Quantités et charge</Bouton>
      </div>

      {!plans.length && (
        <Panneau>
          <EtatVide icone={<FileImage />} titre={piecesDisponibles.length ? 'Aucun plan pour ce dossier' : 'Aucune pièce PDF ou image dans ce dossier'}
            texte={piecesDisponibles.length ? 'Choisissez une pièce du dossier comme fond de plan pour calibrer l’échelle et tracer les cheminements.' : 'Déposez le plan (PDF ou image) dans les pièces du dossier, puis revenez ici.'}
            action={piecesDisponibles.length && ecriture ? <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={() => setNouveau(true)}>Nouveau plan</Bouton>
              : <LienBouton variante="principal" to={`/etudes/${dossierId}/pieces`}>Ouvrir les pièces du dossier</LienBouton>} />
        </Panneau>
      )}

      {plan && <EspacePlan key={plan.id} plan={plan} dossierId={dossierId} />}
      <p className="tres-petit discret">{AIDE_FORMATS}</p>

      <section className="pile" aria-labelledby="cch-recap-titre">
        <h2 id="cch-recap-titre" style={{ fontSize: 'var(--t-xl)' }}>Quantités du dossier</h2>
        <RecapQuantites dossierId={dossierId} />
      </section>

      <NouveauPlan dossierId={dossierId} pieces={piecesDisponibles} ouvert={nouveau} fermer={() => setNouveau(false)} cree={(id) => { setNouveau(false); choisirPlan(id); }} />
    </div>
  );
}

export function NouveauPlan({ dossierId, pieces, ouvert, fermer, cree }:
  { dossierId: string; pieces: PieceDisponible[]; ouvert: boolean; fermer: () => void; cree: (id: string) => void }) {
  const qc = useQueryClient();
  const [versionId, setVersionId] = useState('');
  const [page, setPage] = useState('1');
  const [nom, setNom] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => { if (ouvert) { setVersionId(pieces[0]?.versionId ?? ''); setPage('1'); setNom(pieces[0]?.titre ?? ''); setErreur(null); } }, [ouvert]); // eslint-disable-line react-hooks/exhaustive-deps
  const piece = pieces.find((p) => p.versionId === versionId);
  const n = Number(page);
  const pageOk = Number.isInteger(n) && n >= 1 && (!piece?.pages || n <= piece.pages) && (piece?.mime === 'application/pdf' || n === 1);
  const creer = async () => {
    setEnvoi(true); setErreur(null);
    try {
      const p = await api.post<PlanVue>(`/api/dossiers/${dossierId}/plans`, { versionId, page: n, nom: nom.trim() });
      await qc.invalidateQueries({ queryKey: ['conception-plans', dossierId] });
      cree(p.id);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Nouveau plan" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={!versionId || !pageOk || !nom.trim()} chargement={envoi} onClick={creer}>Créer le plan</Bouton></>}>
      <div className="pile">
        <ChampChoix libelle="Pièce du dossier" value={versionId} onChange={(e) => { setVersionId(e.target.value); const p = pieces.find((x) => x.versionId === e.target.value); if (p) setNom(p.titre); setPage('1'); }}
          options={pieces.map((p) => ({ valeur: p.versionId, libelle: `${p.titre} — ${p.fichier} (version ${p.version})` }))} aide={AIDE_FORMATS} />
        {piece?.mime === 'application/pdf' && (
          <Champ libelle="Page du PDF" inputMode="numeric" value={page} onChange={(e) => setPage(e.target.value)} erreur={pageOk ? null : `Page entre 1 et ${piece.pages ?? '…'}`}
            aide={piece.pages ? `Le document compte ${piece.pages} page(s).` : undefined} />
        )}
        <Champ libelle="Nom du plan" value={nom} maxLength={120} onChange={(e) => setNom(e.target.value)} placeholder="Ex. : rez-de-chaussée, lot CVC" />
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      </div>
    </Dialogue>
  );
}

function StatutEchelle({ plan }: { plan: PlanVue }) {
  const e = plan.echelle;
  if (e.statut === 'aucune') return <Badge ton="ambre">Aucune échelle</Badge>;
  if (e.statut === 'proposee') return <Badge ton="ambre">Échelle proposée, à valider</Badge>;
  return <Badge ton="succes">Échelle validée</Badge>;
}

function EspacePlan({ plan, dossierId }: { plan: PlanVue; dossierId: string }) {
  const { peut } = useSession();
  const ecriture = peut('conception.ecrire');
  const peutValiderEchelle = peut('echelle.valider');
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { confirmer, element: confirmation } = useConfirmation();
  const vis = useRef<VisionneusePoignee>(null);
  const [outil, setOutil] = useState<Outil>({ type: 'consulter' });
  const [route, setRoute] = useState<string | null>(null);
  const [point, setPoint] = useState<number | null>(null);
  const [apercu, setApercu] = useState<{ route: string; points: Point[] } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [distance, setDistance] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [dialogueChem, setDialogueChem] = useState<Point[] | null>(null);
  const [parametres, setParametres] = useState<CheminementVue | null>(null);
  const minuteur = useRef<number | undefined>(undefined);
  const chem = useQuery({ queryKey: ['conception-chem', plan.id], queryFn: () => api.get<{ fiable: boolean; cheminements: CheminementVue[] }>(`/api/plans/${plan.id}/cheminements`) });
  const cheminements = chem.data?.cheminements ?? [];
  const choisi = cheminements.find((c) => c.id === route) ?? null;
  // Rôle référent technique (ou administrateur) : seuls habilités à la validation technique d'un cheminement (contrôlé par le serveur).
  const peutValiderChem = ecriture && peut('echelle.valider');

  const rafraichir = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['conception-chem', plan.id] }),
      qc.invalidateQueries({ queryKey: ['conception-plans', dossierId] }),
      qc.invalidateQueries({ queryKey: ['conception-quantites', dossierId] }),
    ]);
  };
  const agir = async <T,>(fn: () => Promise<T>, succes?: string) => {
    setOccupe(true);
    try { const r = await fn(); await rafraichir(); if (succes) notifier(succes); return r; }
    catch (e) { notifier(messageErreur(e), true); return undefined; }
    finally { setOccupe(false); }
  };

  const poser = (p: Point) => {
    if (outil.type === 'calibrer') setOutil({ ...outil, points: outil.points.length >= 2 ? [p] : [...outil.points, p] });
    else if (outil.type === 'tracer') { if (outil.points.length < 2000) setOutil({ ...outil, points: [...outil.points, p] }); }
  };

  const proposerEchelle = async () => {
    if (outil.type !== 'calibrer' || outil.points.length !== 2) return;
    const d = lireNombre(distance);
    if (d === null || d <= 0) { notifier('Saisissez la distance réelle en mètres (nombre positif).', true); return; }
    const r = await agir(() => api.put(`/api/plans/${plan.id}/echelle`, { points: outil.points, distanceM: d }),
      plan.echelle.statut === 'validee' ? 'Nouvelle échelle proposée : la validation précédente est annulée.' : 'Échelle proposée. Elle doit être validée par un référent technique.');
    if (r) { setOutil({ type: 'consulter' }); setDistance(''); }
  };

  const terminerTrace = async () => {
    if (outil.type !== 'tracer') return;
    if (outil.prolonger) {
      const c = cheminements.find((x) => x.id === outil.prolonger);
      if (!c || !outil.points.length) { setOutil({ type: 'consulter' }); return; }
      const r = await agir(() => api.patch(`/api/plans/${plan.id}/cheminements/${c.id}`, { points: [...c.points, ...outil.points] }), 'Tracé prolongé.');
      if (r) setOutil({ type: 'consulter' });
    } else {
      if (outil.points.length < 2) return;
      setDialogueChem(outil.points);
    }
  };

  const deplacerPoint = (rid: string, i: number, p: Point, fin: boolean) => {
    const c = cheminements.find((x) => x.id === rid); if (!c) return;
    const base = apercu?.route === rid ? apercu.points : c.points;
    const points = base.map((q, j) => (j === i ? p : q));
    setApercu({ route: rid, points });
    if (!fin) return;
    window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(async () => {
      await agir(() => api.patch(`/api/plans/${plan.id}/cheminements/${rid}`, { points }));
      setApercu(null);
    }, 500);
  };
  useEffect(() => () => window.clearTimeout(minuteur.current), []);

  const majChamp = (id: string, champ: string, valeur: string) => {
    const corps: Record<string, unknown> = {};
    if (champ === 'nom') corps.nom = valeur;
    else if (champ === 'typeCable') corps.typeCable = valeur || null;
    else corps[champ] = lireNombre(valeur);
    void agir(() => api.patch(`/api/plans/${plan.id}/cheminements/${id}`, corps));
  };

  const supprimerChem = async (c: CheminementVue) => {
    if (!(await confirmer('Supprimer ce cheminement ?', `« ${c.nom} » et ses ${c.points.length} points seront supprimés définitivement.`, 'Supprimer le cheminement'))) return;
    await agir(() => api.del(`/api/plans/${plan.id}/cheminements/${c.id}`), 'Cheminement supprimé.');
    setRoute(null); setPoint(null);
  };
  const supprimerPoint = async () => {
    if (!choisi || point === null || choisi.points.length <= 2) return;
    await agir(() => api.patch(`/api/plans/${plan.id}/cheminements/${choisi.id}`, { points: choisi.points.filter((_, i) => i !== point) }), 'Point supprimé.');
    setPoint(null);
  };
  const supprimerPlan = async () => {
    if (!(await confirmer('Supprimer ce plan ?', `Le plan « ${plan.nom} », son échelle et ses ${plan.nbCheminements} cheminement(s) seront supprimés. La pièce du dossier est conservée.`, 'Supprimer le plan'))) return;
    await agir(() => api.del(`/api/plans/${plan.id}`), 'Plan supprimé.');
  };

  const e = plan.echelle;
  const instructions = outil.type === 'calibrer'
    ? (outil.points.length < 2 ? `Cliquez sur deux points dont vous connaissez la distance réelle (une cote, une trame de poteaux). Point ${outil.points.length + 1} sur 2.` : 'Saisissez la distance réelle entre les deux points.')
    : outil.type === 'tracer' ? `Cliquez sur le plan, point par point, le long du passage. ${outil.points.length} point(s) posé(s).` : null;

  const actionPrincipale: 'calibrer' | 'tracer' | null = !ecriture ? null : e.statut === 'aucune' ? 'calibrer'
    : e.statut === 'proposee' && peutValiderEchelle ? null : 'tracer';

  return (
    <div className="pile">
      {outil.type === 'consulter' ? (
        <div className="cch-barre" role="toolbar" aria-label="Actions sur le plan">
          {e.statut === 'proposee' && peutValiderEchelle && (
            <Bouton variante="principal" icone={<Check aria-hidden />} chargement={occupe}
              onClick={() => agir(() => api.post(`/api/plans/${plan.id}/echelle/validation`, { mParUnite: plan.echelle.mParUnite }), 'Échelle validée : les métrés de ce plan sont fiables.')}>Valider l’échelle</Bouton>
          )}
          {ecriture && <Bouton variante={actionPrincipale === 'tracer' ? 'principal' : 'secondaire'} icone={<PenLine aria-hidden />}
            onClick={() => { setOutil({ type: 'tracer', points: [], prolonger: null }); setRoute(null); setPoint(null); }}>Tracer un cheminement</Bouton>}
          {ecriture && <Bouton variante={actionPrincipale === 'calibrer' ? 'principal' : 'secondaire'} icone={<Ruler aria-hidden />}
            onClick={() => { setOutil({ type: 'calibrer', points: [] }); setRoute(null); setPoint(null); }}>{e.statut === 'aucune' ? 'Calibrer l’échelle' : 'Recalibrer l’échelle'}</Bouton>}
          <span className="cch-barre-fin"><StatutEchelle plan={plan} /></span>
        </div>
      ) : (
        <div className="cch-consigne pile" role="group" aria-label={outil.type === 'calibrer' ? 'Calibration de l’échelle' : 'Tracé en cours'}>
          <p role="status">{instructions}</p>
          {outil.type === 'calibrer' ? (
            <div className="cch-barre">
              {outil.points.length === 2 && (
                <div className="cch-distance">
                  <Champ libelle="Distance réelle entre les deux points (m)" inputMode="decimal" value={distance} autoFocus
                    onChange={(ev) => setDistance(ev.target.value)} onKeyDown={(ev) => { if (ev.key === 'Enter') void proposerEchelle(); }} placeholder="Ex. : 7,20" />
                </div>
              )}
              <Bouton variante="principal" icone={<Check aria-hidden />} disabled={outil.points.length !== 2 || !distance.trim()} chargement={occupe} onClick={proposerEchelle}>Proposer l’échelle</Bouton>
              <Bouton icone={<X aria-hidden />} onClick={() => { setOutil({ type: 'consulter' }); setDistance(''); }}>Annuler</Bouton>
            </div>
          ) : (
            <div className="cch-barre">
              <Bouton variante="principal" icone={<Check aria-hidden />} disabled={outil.prolonger ? outil.points.length < 1 : outil.points.length < 2} chargement={occupe} onClick={terminerTrace}>Terminer</Bouton>
              <Bouton icone={<Undo2 aria-hidden />} disabled={!outil.points.length} onClick={() => setOutil({ ...outil, points: outil.points.slice(0, -1) })}>Retirer le dernier point</Bouton>
              <Bouton icone={<X aria-hidden />} onClick={() => setOutil({ type: 'consulter' })}>Annuler le tracé</Bouton>
            </div>
          )}
        </div>
      )}

      <RattachementEtage plan={plan} />
      {e.statut !== 'validee' && (
        <Alerte ton="ambre" titre={e.statut === 'aucune' ? 'Aucune échelle : longueurs non calculées' : 'Métrés non fiables : échelle non validée'}>
          {e.statut === 'aucune' ? 'Calibrez l’échelle du plan pour obtenir des longueurs. Elles resteront non fiables tant qu’un référent technique ne l’aura pas validée.'
            : 'Les longueurs affichées sont indicatives. Elles ne peuvent pas être envoyées au devis tant que l’échelle n’est pas validée.'}
          {e.statut === 'proposee' && !peutValiderEchelle ? ' La validation est réservée au référent technique.' : ''}
        </Alerte>
      )}
      {plan.versionRemplacee && <Alerte ton="ambre" titre="Une version plus récente de cette pièce existe">Ce plan repose sur une version remplacée de la pièce. Créez un plan sur la version en vigueur.</Alerte>}

      <div className="cch-plan-disposition">
        <div className="pile" style={{ gap: 8, minWidth: 0 }}>
          <div className="cch-barre" role="toolbar" aria-label="Affichage du plan">
            <div className="groupe-btn cch-zoom" role="group" aria-label="Zoom">
              <Bouton className="btn-icone" icone={<ZoomOut aria-hidden />} onClick={() => vis.current?.zoomer(1 / 1.25)} aria-label="Zoom arrière" />
              <span className="tres-petit chiffre cch-zoom-valeur" aria-live="polite">{Math.round(zoom * 100)} %</span>
              <Bouton className="btn-icone" icone={<ZoomIn aria-hidden />} onClick={() => vis.current?.zoomer(1.25)} aria-label="Zoom avant" />
              <Bouton icone={<Maximize2 aria-hidden />} onClick={() => vis.current?.ajuster()}>Ajuster</Bouton>
            </div>
            {plan.piece && <span className="tres-petit discret cch-barre-fin">{plan.piece.titre} · page {plan.page} · version {plan.piece.version}</span>}
          </div>
          <VisionneusePlan ref={vis} plan={plan} cheminements={cheminements} outil={outil} poser={poser} route={route} point={point}
            choisirRoute={setRoute} choisirPoint={setPoint} deplacerPoint={deplacerPoint} apercu={apercu} lectureSeule={!ecriture} surZoom={setZoom} />
          <p className="tres-petit discret">Ctrl + molette pour zoomer. Au clavier : Tab jusqu’au plan, flèches pour placer le curseur, Entrée pour poser un point.</p>
        </div>

        <aside className="pile cch-outils" aria-label="Échelle et cheminement choisi">
          <Panneau titre="Échelle" actions={<StatutEchelle plan={plan} />}>
            {e.statut === 'aucune' ? <p className="petit discret">Aucune échelle. Calibrez-la à partir d’une cote connue du plan.</p> : (
              <dl className="fiche cch-fiche-1">
                <div><dt>Segment de référence</dt><dd>{e.distanceM ? `${Number(e.distanceM).toLocaleString('fr-FR')} m` : <span className="inconnu">Inconnu</span>}</dd></div>
                <div><dt>Proposée</dt><dd>{e.proposeePar ?? 'Inconnu'}{e.proposeeLe ? ` · ${dateHeure(e.proposeeLe)}` : ''}</dd></div>
                {e.statut === 'validee' && <div><dt>Validée</dt><dd>{e.valideePar} · {dateHeure(e.valideeLe)}</dd></div>}
              </dl>
            )}
            {e.statut === 'validee' && ecriture && <p className="tres-petit discret" style={{ marginTop: 8 }}>Recalibrer annule la validation.</p>}
          </Panneau>

          <Panneau titre="Cheminement choisi">
            {!choisi ? <p className="petit discret">Choisissez un tracé sur le plan ou dans le tableau pour le modifier.</p> : (
              <div className="pile" style={{ gap: 8 }}>
                <p className="petit"><strong>{choisi.nom}</strong> · {choisi.points.length} points{point !== null ? ` · point ${point + 1} choisi` : ''}</p>
                {ecriture && <p className="tres-petit discret">Glissez une poignée pour déplacer un point, ou choisissez-la au clavier (Tab) et déplacez-la avec les flèches.</p>}
                {ecriture && (
                  <div className="groupe-btn">
                    <Bouton compact onClick={() => setParametres(choisi)}>Paramètres du cheminement</Bouton>
                    <Bouton compact disabled={outil.type !== 'consulter'} onClick={() => setOutil({ type: 'tracer', points: [], prolonger: choisi.id })}>Prolonger le tracé</Bouton>
                    <Bouton compact disabled={point === null || choisi.points.length <= 2} onClick={supprimerPoint}
                      title={choisi.points.length <= 2 ? 'Un cheminement garde au moins deux points.' : undefined}>Supprimer le point choisi</Bouton>
                    <Bouton compact variante="danger" icone={<Trash2 aria-hidden />} onClick={() => supprimerChem(choisi)}>Supprimer</Bouton>
                  </div>
                )}
              </div>
            )}
            <p className="tres-petit discret" style={{ marginTop: 8 }}>Les parcours tracés restent modifiables et soumis à validation technique par un référent.</p>
          </Panneau>
          {ecriture && <Bouton variante="danger" compact icone={<Trash2 aria-hidden />} onClick={supprimerPlan}>Supprimer ce plan</Bouton>}
        </aside>
      </div>

      <section className="pile" style={{ gap: 8 }} aria-labelledby="cch-chem-titre">
        <div className="rangee entre">
          <h3 id="cch-chem-titre" className="cch-titre-tableau">Cheminements du plan <span className="compteur">{cheminements.length}</span></h3>
          {!chem.data?.fiable && cheminements.length > 0 && <Badge ton="ambre">{e.statut === 'aucune' ? 'Longueurs non calculées' : 'Métrés non fiables : échelle non validée'}</Badge>}
        </div>
        {chem.isLoading ? <Chargement lignes={2} /> : chem.isError ? <EtatErreur erreur={chem.error} reessayer={() => chem.refetch()} /> : (
          <TableauCheminements cheminements={cheminements} route={route} choisir={(id) => { setRoute(id); setPoint(null); setOutil({ type: 'consulter' }); }}
            ecriture={ecriture} peutValider={peutValiderChem} maj={majChamp}
            valider={(id, ok) => agir(() => api.post(`/api/plans/${plan.id}/cheminements/${id}/validation`, { valide: ok, majLe: cheminements.find((c) => c.id === id)?.majLe, empreinte: cheminements.find((c) => c.id === id)?.empreinte }), ok ? 'Cheminement validé.' : 'Validation retirée.')}
            supprimer={supprimerChem} />
        )}
        <p className="tres-petit discret">
          Longueur plane = somme des segments tracés × échelle. Par câble = plane + verticales. Total posé = par câble × nombre de câbles.
          Réserve et chutes sont calculées séparément sur le total posé ; « à commander » = total posé + réserve + chutes.
        </p>
      </section>

      <DialogueParametres c={parametres} dossierId={dossierId} fermer={() => setParametres(null)} enregistrer={async (b) => {
        const r = await agir(() => api.patch(`/api/plans/${plan.id}/cheminements/${parametres!.id}`, b), 'Paramètres enregistrés : validation à refaire.');
        if (r) setParametres(null);
      }} />
      <NouveauCheminement dossierId={dossierId} planId={plan.id} points={dialogueChem} precedent={cheminements.at(-1)} numero={cheminements.length + 1} fermer={() => setDialogueChem(null)}
        creer={async (corps) => {
          const r = await agir(() => api.post<CheminementVue>(`/api/plans/${plan.id}/cheminements`, { ...corps, points: dialogueChem }), 'Cheminement enregistré.');
          if (r) { setDialogueChem(null); setOutil({ type: 'consulter' }); setRoute(r.id); }
        }} />
      {confirmation}
    </div>
  );
}

function NouveauCheminement({ points, precedent, numero, fermer, creer, dossierId, planId }: {
  dossierId: string; planId: string;
  points: Point[] | null; precedent?: CheminementVue; numero: number; fermer: () => void;
  creer: (c: { nom: string; typeCable: string | null; nbCables: number; reservePct: number; chutesPct: number; verticalesM: number; nature?: string; refrigeration?: Refrigeration; verticale?: VerticalSpec }) => Promise<void>;
}) {
  const [f, setF] = useState({ nom: '', typeCable: '', nbCables: '1', reservePct: '0', chutesPct: '0', verticalesM: '0' });
  const [envoi, setEnvoi] = useState(false);
  const [nature, setNature] = useState('cable'), [frigo, setFrigo] = useState(liaisonVide), [verticale, setVerticale] = useState(verticaleVide);
  useEffect(() => {
    if (!points) return;
    setNature('cable'); setFrigo(liaisonVide()); setVerticale(verticaleVide());
    // Paramètres repris du cheminement précédent du plan (cohérence de saisie) ; sinon aucune réserve ni chute supposée.
    setF({ nom: `Cheminement ${numero}`, typeCable: precedent?.typeCable ?? '', nbCables: String(precedent?.nbCables ?? 1),
      reservePct: precedent ? String(Number(precedent.reservePct)).replace('.', ',') : '0', chutesPct: precedent ? String(Number(precedent.chutesPct)).replace('.', ',') : '0',
      verticalesM: '0' });
  }, [!!points]); // eslint-disable-line react-hooks/exhaustive-deps
  const n = { nb: lireNombre(f.nbCables), res: lireNombre(f.reservePct), chu: lireNombre(f.chutesPct), ver: lireNombre(f.verticalesM) };
  const err = {
    nb: n.nb === null || !Number.isInteger(n.nb) || n.nb < 1 || n.nb > 500 ? 'Nombre entier entre 1 et 500' : null,
    res: n.res === null || n.res < 0 || n.res > 100 ? 'Pourcentage entre 0 et 100' : null,
    chu: n.chu === null || n.chu < 0 || n.chu > 100 ? 'Pourcentage entre 0 et 100' : null,
    ver: n.ver === null || n.ver < 0 || n.ver > 10000 ? 'Longueur positive en mètres' : null,
  };
  const ok = f.nom.trim() && !err.res && !err.chu && (nature === 'frigorifique' || (!err.nb && !err.ver));
  const maj = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Dialogue titre="Enregistrer le cheminement" ouvert={!!points} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Reprendre le tracé</Bouton><Bouton variante="principal" disabled={!ok} chargement={envoi}
        onClick={async () => { setEnvoi(true); try { await creer({ nom: f.nom.trim(), typeCable: nature === 'cable' ? f.typeCable.trim() || null : null,
          nbCables: nature === 'frigorifique' ? 1 : n.nb!, reservePct: n.res!, chutesPct: n.chu!, verticalesM: nature === 'frigorifique' ? 0 : n.ver!,
          ...(nature === 'frigorifique' ? { nature, refrigeration: { ...frigo, unitesInterieures: frigo.unitesInterieures.filter(Boolean) }, verticale } : {}) }); } finally { setEnvoi(false); } }}>
        Enregistrer</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire">{points?.length ?? 0} points tracés. Les longueurs sont calculées par le serveur à partir de l’échelle du plan.</p>
        <Champ libelle="Nom" value={f.nom} maxLength={120} onChange={maj('nom')} />
        <ChampChoix libelle="Nature du tracé" value={nature} onChange={(e) => { setNature(e.target.value); if (e.target.value === 'frigorifique') setF((x) => ({ ...x, reservePct: '', chutesPct: '' })); }}
          options={[{ valeur: 'cable', libelle: 'Câble' }, { valeur: 'frigorifique', libelle: 'Liaison frigorifique' }]} />
        {nature === 'cable' && <Champ libelle="Type de câble" value={f.typeCable} maxLength={120} onChange={maj('typeCable')} list="cch-types-cable-dialogue"
          aide="Reprenez la référence du catalogue pour récupérer son prix dans le devis." />}
        {nature === 'frigorifique' && <><ChampsFrigorifiques f={frigo} changer={setFrigo} nbPoints={points?.length ?? 0} /><ChampsVerticale v={verticale} changer={setVerticale} dossierId={dossierId} planId={planId} /></>}
        <datalist id="cch-types-cable-dialogue">{TYPES_CABLE_SUGGERES.map((t) => <option key={t} value={t} />)}</datalist>
        <div className="grille-form cch-grille-2">
          {nature === 'cable' && <><Champ libelle="Nombre de câbles" inputMode="numeric" value={f.nbCables} onChange={maj('nbCables')} erreur={err.nb} />
          <Champ libelle="Verticales par câble (m)" inputMode="decimal" value={f.verticalesM} onChange={maj('verticalesM')} erreur={err.ver}
            aide="Descentes et remontées, saisies à part." /></>}
          <Champ libelle="Réserve (%)" inputMode="decimal" value={f.reservePct} onChange={maj('reservePct')} erreur={err.res} />
          <Champ libelle="Chutes (%)" inputMode="decimal" value={f.chutesPct} onChange={maj('chutesPct')} erreur={err.chu} />
        </div>
      </div>
    </Dialogue>
  );
}
